// Updated Dashboard component with "Submit Documents" link below the score section

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { API_URL } from "./constants";

// --- Interfaces ---
interface InsurancePlan {
  name: string;
  price: string;
  coverage: string;
}

interface User {
  full_name?: string;
}

interface Business {
  business_name?: string;
}

interface Assessment {
  id: number;
  score: number | null;
  score_tier: string | null;
  status: string;
}

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<InsurancePlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        navigate("/login");
        return;
      }

      const storedUser = localStorage.getItem("user");
      const storedBusiness = localStorage.getItem("business");
      if (storedUser) setUser(JSON.parse(storedUser));
      if (storedBusiness) setBusiness(JSON.parse(storedBusiness));

      try {
        const res = await fetch(`${API_URL}/api/assessment/latest`, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          setAssessment(data.assessment);
          if (data.assessment && data.assessment.status === "completed") {
            await fetchRecommendedPlans(token);
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const fetchRecommendedPlans = async (token: string) => {
    setPlansLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/plans/recommended`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const formattedPlans: InsurancePlan[] = (data.plans || []).map((plan: any) => ({
          name: plan.name,
          price: formatCurrency(plan.premium) + " / yr",
          coverage: formatCoverage(plan.coverage_limit)
        }));
        setPlans(formattedPlans);
      }
    } catch (err) {
      console.error("Failed to fetch plans:", err);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCoverage = (amount: number) => {
    if (amount >= 1000000) {
      return `Up to $${(amount / 1000000).toFixed(1)}M`;
    }
    return `Up to ${formatCurrency(amount)}`;
  };

  const handleStartAssessment = async () => {
    const token = localStorage.getItem("token");
    
    try {
      const res = await fetch(`${API_URL}/api/assessment/start`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.ok) {
        const data = await res.json();
        navigate(`/assessment/${data.assessment_id}`);
      } else {
        const error = await res.json();
        alert(error.error || "Failed to start assessment");
      }
    } catch (err) {
      console.error("Failed to start assessment:", err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("business");
    navigate("/login");
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#22c55e";
    if (score >= 60) return "#eab308";
    if (score >= 40) return "#f97316";
    return "#ef4444";
  };

  const getRiskLabel = (tier: string | null) => {
    switch (tier) {
      case "low_risk": return "Low Risk";
      case "medium_risk": return "Moderate Risk";
      case "high_risk": return "High Risk";
      case "critical_risk": return "Critical Risk";
      default: return "Unknown";
    }
  };

  const firstName = user?.full_name?.split(" ")[0] || "User";

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const score = assessment?.score ? Number(assessment.score) : 0;
  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Welcome, {firstName}</h1>
            <p>Business: <strong>{business?.business_name || "Not set"}</strong></p>
          </div>
          <img 
            src="/scryer-logo-x500.png" 
            alt="Scryer Cyber Insurance" 
            className="header-logo"
          />
        </div>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </header>

      <main className="dashboard-main">
        <div className="score-section">
          {assessment?.status === "completed" && assessment.score !== null ? (
            <div className="score-card">
              <h2>Cyber Liability Score</h2>
              <div className="score-circle-container">
                <svg className="score-circle" width="220" height="220" viewBox="0 0 220 220">
                  <circle cx="110" cy="110" r="90" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                  <circle
                    cx="110"
                    cy="110"
                    r="90"
                    fill="none"
                    stroke={getScoreColor(score)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    transform="rotate(-90 110 110)"
                    style={{ transition: "stroke-dashoffset 1s ease-in-out" }}
                  />
                </svg>
                <div className="score-number" style={{ color: getScoreColor(score) }}>
                  {Math.round(score)}
                </div>
              </div>
              <div className="risk-level">
                <span>Risk Level:</span> {getRiskLabel(assessment.score_tier)}
              </div>

              {/* --- Added Submit Documents Link --- */}
              <div className="submit-documents" style={{ marginTop: "1rem" }}>
                <button
                  onClick={() => navigate("/submitDocument")}
                  className="secondary-btn"
                >
                  Improve your score by submitting relevant documents
                </button>
              </div>
            </div>
          ) : assessment?.status === "in_progress" ? (
            <div className="score-card no-score">
              <h2>Assessment In Progress</h2>
              <p>Continue your assessment to get your score.</p>
              <button onClick={() => navigate(`/assessment/${assessment.id}`)} className="primary-btn">
                Continue Assessment
              </button>
            </div>
          ) : (
            <div className="score-card no-score">
              <h2>Get Your Cyber Score</h2>
              <p>Complete a quick assessment to discover your cyber risk level.</p>
              <button onClick={handleStartAssessment} className="primary-btn">
                Start Assessment
              </button>
            </div>
          )}
        </div>

        <div className="plans-section">
          <h2>Available Insurance Plans</h2>

          {plansLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loader" style={{ margin: '0 auto 1rem' }}></div>
              <p style={{ color: '#6b7280' }}>Loading recommendations...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="no-plans">
              <p style={{ color: '#6b7280' }}>
                {assessment?.status === "completed" 
                  ? "No plans available at this time. Please check back later."
                  : "Complete your assessment to see personalized insurance plan recommendations based on your cyber risk profile."}
              </p>
            </div>
          ) : (
            <>
              <div className="plans-list">
                {plans.map((plan, index) => (
                  <div 
                    key={index} 
                    className={`plan-card ${selectedPlan === index ? "selected" : ""}`}
                    onClick={() => setSelectedPlan(index)}
                  >
                    <h3>{plan.name}</h3>
                    <p className="coverage">{plan.coverage}</p>
                    <p className="price">{plan.price}</p>
                  </div>
                ))}
              </div>
              {selectedPlan !== null && (
                <button className="select-plan-btn">
                  Select {plans[selectedPlan].name}
                </button>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
